import { ticketingService } from 'services/api';
import TenantPage from 'components/common/TenantPage';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { ITicket } from 'interfaces';
import { useEffect, useState } from 'react';
import TicketForm from '../TicketForm';
import {
  Button,
  ButtonGroup,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle
} from '@mui/material';
import EntityHistory from 'components/widgets/EntityHistory';
import toast from 'features/toast';
import { IsBottomFromTechnicianCoordinator } from 'components/helpers/IsBottomFromTechnicianCoordinator';
import LockOutlined from '@mui/icons-material/LockOutlined';
import CloseTicketForm from './CloseTicketForm';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import { DocumentTableCardComponent } from 'components/common/documentLM/DocumentTableCardComponent';
import ShowEmailTiketingDialog from '../dialog/ShowEmailTiketingDialog';
import PostAddIcon from '@mui/icons-material/PostAdd';
import DownloadIcon from '@mui/icons-material/Download';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
dayjs.extend(customParseFormat);
import { isTenantAdministrator } from 'components/helpers/IsTenantAdministrator';
import { IsSuperAdminGuard } from 'components/helpers/IsSuperAdmin';

export const Ticket = () => {
  const translationPrefix = 'pages.tickets';
  const { t } = useTranslation();

  const { ticketId } = useParams();
  const [ticket, setTicket] = useState<ITicket>();

  const [openDialog, setOpenDialog] = useState(false);

  // --------------------------------------------------------- get
  useEffect(() => {
    ticketingService.get(+ticketId).then(setTicket);
  }, []);

  // --------------------------------------------------------- approve ticket
  const approveTicket = () => {
    ticketingService.approvedByUser(+ticketId).then(() => {
      toast.success(t(`${translationPrefix}.ticket-approved`));
      ticketingService.get(+ticketId).then(setTicket);
    });
  };

  // --------------------------------------------------------- update ticket
  const updateTicket = (values) => {
    // bisogna escludere contract_equipment altrimenti da errore (contract_equipment è l'id in UPDATE ma qui si mostra il equipment_code)
    delete values['contract_equipment'];
    // if (values.created_at) {
    //   const parsed = dayjs(values.created_at, 'DD/MM/YYYY HH:mm');
    //   values.created_at = parsed;
    // }
    ticketingService.update(+ticketId, values).then((res) => {
      toast.success(t(`${translationPrefix}.ticket-updated`));
      setTicket(res);
    });
  };

  // --------------------------------------------------------- dialog close ticket
  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const closeTicket = (values) => {
    ticketingService.closeTicket(+ticketId, values).then((res) => {
      toast.success(t(`${translationPrefix}.ticket-closed`));
      ticketingService.get(+ticketId).then(setTicket);
    });
    setOpenDialog(false);
  };

  // --------------------------------------------------------- send ticket
  const sendTicket = (values) => {
    ticketingService.sendTicket(+ticketId, values).then(() => {
      toast.success(t(`${translationPrefix}.sent`));
    });
  };

  // --------------------------------------------------------- generate PDF
  const generateEmailPdf = () => {
    ticketingService.generateEmailPdf(+ticketId).then((res) => {
      toast.success(t(`${translationPrefix}.email-pdf-generated`));
    });
  };

  const downloadEmailPdf = () => {
    ticketingService.downloadEmailPdf(+ticketId).then((res) => {
      toast.success(t(`actions.successfully.downloaded`));
    });
  };

  const canViewToApprove = IsSuperAdminGuard() || isTenantAdministrator();

  return (
    <TenantPage
      title={t(`Ticket #${ticketId}`)}
      subtitle={t(`${translationPrefix}.subtitle`)}
      entityCode={`#${ticketId}`}
      menuRight={
        <div className="flex-row justify-end">
          <IsBottomFromTechnicianCoordinator>
            {ticket && (
              <ButtonGroup>
                {!ticket.approvation_status && canViewToApprove && (
                  <Button variant="outlined" size="small" color="info" onClick={approveTicket}>
                    <CheckCircleOutlineOutlinedIcon className="mr-2" />
                    {t(`${translationPrefix}.approve`)}
                  </Button>
                )}
                {ticket.approvation_status && (
                  <div className="flex flex-wrap">
                    <div className="">
                      <ButtonGroup size="small" className="mr-4">
                        <Button sx={{ pointerEvents: 'none' }}>
                          {t(`${translationPrefix}.email-pdf`)}
                        </Button>
                        <>
                          <Button onClick={generateEmailPdf}>
                            <PostAddIcon />
                          </Button>
                          <Button onClick={downloadEmailPdf}>
                            <DownloadIcon />
                          </Button>
                        </>
                      </ButtonGroup>
                    </div>
                    <div>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={handleOpenDialog}
                        disabled={
                          !(
                            ['OPEN', 'INTERVENTION_REQUESTED'].indexOf(ticket.status.display_name) >
                            -1
                          )
                        }>
                        <LockOutlined className="mr-2" />
                        {t(`${translationPrefix}.close`)}
                      </Button>
                    </div>
                    <div>
                      <ShowEmailTiketingDialog
                        hasBeenSent={ticket.sent}
                        equipment_id={ticket.equipment_id}
                        equipment_cc_email={ticket.equipment_cc_ticketing_email}
                        equipment_email={ticket.equipment_email}
                        warehouse_code={ticket.warehouse_code}
                        warehouse_phone={ticket.warehouse_phone}
                        onSubmit={sendTicket}
                      />
                    </div>
                  </div>
                )}
              </ButtonGroup>
            )}
          </IsBottomFromTechnicianCoordinator>
        </div>
      }>
      <div className="flex flex-wrap">
        <div className="w-full p-4">
          <TicketForm ticket={ticket} onSubmit={updateTicket} />
        </div>

        {ticket && (
          <div className="w-full p-4">
            <Card variant="outlined">
              <DocumentTableCardComponent
                originalService={'ticket'}
                objectId={ticket.id}
                dictParams={{ ticket: ticketId }}
                folder={'menu.tickets/' + ticket.equipment + '_' + ticketId}
                folder_description={`Strumento: ${ticket.equipment}; Data chiusura: ${
                  ticket.resolved_at ?? 'N.D.'
                }; Conforme: ${ticket.compliant ? `SI` : `NO`}; `}
              />
            </Card>
          </div>
        )}

        <div className="w-full md:w-1/2 p-4">
          {ticket && (
            <Card variant="outlined">
              <CardContent>
                <EntityHistory service={ticketingService} entityId={ticket.id} />
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Dialog close ticketing */}
      <Dialog open={openDialog} maxWidth="lg" fullWidth>
        <DialogTitle>{t(`${translationPrefix}.close`)}</DialogTitle>
        <DialogContent>
          <CloseTicketForm onSubmit={closeTicket} />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="error">
            {t('actions.cancel')}
          </Button>
          <Button type="submit" form="closeTicket">
            {t(`actions.confirm`)}
          </Button>
        </DialogActions>
      </Dialog>
    </TenantPage>
  );
};

export default Ticket;
